import React, { useMemo, useState, useEffect } from 'react'
import SearchBar from './SearchBar'
import UserListItem from './UserListItem'
import LoadingSpinner from './LoadingSpinner'
import { InvitableUser } from '../api/bookmark'
import Gap from './Gap'
import SheetHeader from './SheetHeader'

interface PlateMemberInviteContentProps {
  searchValue: string
  onSearchChange: (value: string) => void
  pinnedUsers: InvitableUser[]
  searchUsers: InvitableUser[]
  /** 검색 전 추천 목록 = 내가 팔로우하는 사람들 (인스타그램식) */
  suggestedUsers: InvitableUser[]
  isLoadingUsers: boolean
  selectedMembers: string[]
  onMemberToggle: (userId: string, user?: InvitableUser) => void
  onBack: () => void
  onSubmit: () => void
  title?: string
  /** 풀페이지로 쓸 때 true — 리스트가 고정 높이(100vh 기반) 대신 남은 공간을 채운다.
   *  (바텀시트에선 false: 시트가 내용 높이로 열리므로 고정 높이가 필요) */
  fill?: boolean
}

const PlateMemberInviteContent: React.FC<PlateMemberInviteContentProps> = ({
  searchValue,
  onSearchChange,
  pinnedUsers,
  searchUsers,
  suggestedUsers,
  isLoadingUsers,
  selectedMembers,
  onMemberToggle,
  onBack,
  onSubmit,
  title = '멤버 초대',
  fill = false,
}) => {
  const hasSearchQuery = searchValue.trim().length > 0

  const [extraPinnedById, setExtraPinnedById] = useState<
    Record<string, InvitableUser>
  >({})

  useEffect(() => {
    const memberIds = new Set(pinnedUsers.map((u) => u.id))
    setExtraPinnedById((prev) => {
      let changed = false
      const next = { ...prev }
      for (const id of Object.keys(next)) {
        if (memberIds.has(id)) {
          delete next[id]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [pinnedUsers])

  const mergedPinnedUsers = useMemo(() => {
    const ids = new Set(pinnedUsers.map((u) => u.id))
    const extras = Object.values(extraPinnedById).filter((u) => !ids.has(u.id))
    return [...pinnedUsers, ...extras]
  }, [pinnedUsers, extraPinnedById])

  // 검색 전 목록: 멤버/선택됨 + 팔로우 추천(중복 제거).
  // 선택됨/멤버를 항상 상단으로, 그 안에서 닉네임순.
  const previewList = useMemo(() => {
    const map = new Map<string, InvitableUser>()
    mergedPinnedUsers.forEach((u) => map.set(u.id, u))
    suggestedUsers.forEach((u) => {
      if (!map.has(u.id)) map.set(u.id, u)
    })
    const sel = new Set(selectedMembers)
    return Array.from(map.values()).sort((a, b) => {
      const aTop = sel.has(a.id) || a.is_member ? 0 : 1
      const bTop = sel.has(b.id) || b.is_member ? 0 : 1
      if (aTop !== bTop) return aTop - bTop
      return (a.nickname || '').localeCompare(b.nickname || '', 'ko')
    })
  }, [mergedPinnedUsers, suggestedUsers, selectedMembers])

  const handleSearchChange = (value: string) => {
    onSearchChange(value)
  }

  const handleMemberToggle = (user: InvitableUser) => {
    const wasSelected = selectedMembers.includes(user.id)
    const isServerMember = user.is_member === true

    onMemberToggle(user.id, user)

    if (!wasSelected) {
      if (!isServerMember) {
        setExtraPinnedById((prev) => ({ ...prev, [user.id]: user }))
      }
    } else {
      if (!isServerMember) {
        setExtraPinnedById((prev) => {
          if (!(user.id in prev)) {
            return prev
          }
          const next = { ...prev }
          delete next[user.id]

          return next
        })
      }
    }
  }

  const renderUserRow = (user: InvitableUser) => {
    const isSelected = selectedMembers.includes(user.id)
    const isShowXIcon = isSelected || user.is_member
    // 선택/멤버만 선명, 나머지 추천은 흐리게. 검색 중에는 전체 선명.
    const isRowDimmed = !isShowXIcon && !hasSearchQuery

    return (
      <div
        className={`w-full flex items-center transition-opacity duration-200 ${
          isRowDimmed ? 'opacity-40' : 'opacity-100'
        }`}
      >
        <UserListItem
          id={user.id}
          nickname={user.nickname}
          username={user.username}
          profileImage={user.profile_image || undefined}
          actionButtons={
            isShowXIcon ? (
              <div className="flex-shrink-0">
                <button
                  type="button"
                  onClick={() => handleMemberToggle(user)}
                  className="flex items-center justify-center cursor-pointer"
                >
                  <img
                    src="/images/bold-plus-icon.png"
                    alt={'선택 해제'}
                    className={`
                    w-24px h-24px object-contain
                    transition-transform duration-300 ease-in-out
                    rotate-45
                  `}
                  />
                </button>
              </div>
            ) : null
          }
          onClick={() => handleMemberToggle(user)}
        />
      </div>
    )
  }

  const renderScrollAreaContent = () => {
    if (isLoadingUsers) {
      return (
        <div className="w-full flex items-center justify-center py-40px">
          <LoadingSpinner />
        </div>
      )
    }

    // 검색 시엔 결과 전체, 검색 전엔 (멤버/선택 + 팔로우 추천) 프리뷰.
    const list = hasSearchQuery ? searchUsers : previewList

    if (list.length === 0) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center">
          <span className="font-pretendard text-14px text-textColorText2">
            {hasSearchQuery ? '검색 결과가 없습니다' : '초대할 친구를 검색해보세요'}
          </span>
        </div>
      )
    }

    return list.map((user, index) => (
      <React.Fragment key={user.id}>
        {index > 0 && <Gap height={12} />}
        {renderUserRow(user)}
      </React.Fragment>
    ))
  }

  return (
    <div
      className={
        fill
          ? 'w-full h-full flex flex-col min-h-0'
          : 'w-full flex flex-col'
      }
    >
      {/* 헤더 (공용) */}
      <SheetHeader
        title={title}
        leftAction={
          <button
            type="button"
            onClick={onBack}
            className="flex items-center justify-center cursor-pointer"
          >
            <img
              src="/images/back-arrow-icon.png"
              alt="뒤로가기"
              draggable={false}
              className="w-48px h-48px object-contain"
            />
          </button>
        }
        rightAction={
          <button
            type="button"
            onClick={onSubmit}
            className="font-pretendard font-bold text-16px leading-24px text-brown-80%-main cursor-pointer whitespace-nowrap pr-20px"
          >
            완료
          </button>
        }
      />

      <Gap height={16} />

      {/* 검색 */}
      <div className="px-20px w-full">
        <SearchBar
          placeholder="검색"
          value={searchValue}
          onSearch={handleSearchChange}
        />
      </div>

      <Gap height={16} />

      {/* 하나의 리스트 (상단 고정/구분선 없음).
          풀페이지(fill)면 남은 공간을 채우고, 시트면 고정 높이(100vh 기반). */}
      <div
        className={`
          w-full
          flex
          flex-col
          ${fill ? 'flex-1' : 'h-[calc(100vh*480/844)]'}
          min-h-0
          overflow-y-auto
          scrollbar-hide
        `}
      >
        <div
          className={`
            flex-1
            min-h-0
            px-20px
          `}
        >
          {renderScrollAreaContent()}
        </div>

        <Gap height={8} />
      </div>

      {!fill && <Gap height={24} />}
    </div>
  )
}

export default PlateMemberInviteContent
